/**
 * Delivery Form
 * Form for logging deliveries with camera and GPS
 */

import { useState, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Camera, Package, AlertCircle } from 'lucide-react'
import { Button, Textarea, Card } from '../ui'
import { CameraCapture } from '../shared'
import { useGeolocation } from '../../hooks'
import { useAppStore } from '../../stores'

// =============================================================================
// VALIDATION SCHEMA
// =============================================================================

const deliverySchema = z.object({
  note: z.string().optional(),
})

type DeliveryFormData = z.infer<typeof deliverySchema>

// =============================================================================
// COMPONENT
// =============================================================================

export function DeliveryForm() {
  const { addDelivery, setLoading, isLoading } = useAppStore()
  const { location } = useGeolocation()
  
  const [photo, setPhoto] = useState<string | null>(null)
  const [showCamera, setShowCamera] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<DeliveryFormData>({
    resolver: zodResolver(deliverySchema),
    defaultValues: {
      note: '',
    },
  })
  
  const handleCapture = useCallback((imageData: string) => {
    setPhoto(imageData)
    setShowCamera(false)
  }, [])
  
  const handleRetake = useCallback(() => {
    setPhoto(null)
    setShowCamera(true)
  }, [])
  
  const onSubmit = async (data: DeliveryFormData) => {
    try {
      setError(null)
      
      // Validate requirements
      if (!photo) {
        setError('Please take a photo of the delivery')
        return
      }
      
      if (!location) {
        setError('GPS location is required. Please enable location services.')
        return
      }
      
      // Show confirmation dialog
      const confirmed = window.confirm('Are you sure you want to submit this delivery?')
      if (!confirmed) {
        return
      }
      
      setLoading(true)
      
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 500))
      
      // Add delivery to store
      addDelivery({
        timestamp: new Date().toISOString(),
        photo,
        note: data.note || '',
        location: {
          latitude: location.latitude,
          longitude: location.longitude,
          accuracy: location.accuracy,
          timestamp: new Date().toISOString(),
        },
        status: 'delivered',
      })
      
      // Hard refresh the page
      window.location.reload()
    } catch {
      setError('Failed to submit delivery. Please try again.')
      setLoading(false)
    }
  }
  
  return (
    <div className="space-y-6">
      {/* Error Message */}
      {error && (
        <Card className="bg-red-50 border-red-200">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <p className="text-red-700">{error}</p>
          </div>
        </Card>
      )}
      
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Photo Section */}
        <Card>
          <div className="flex items-center gap-2 mb-4">
            <Camera className="w-5 h-5 text-gray-600" />
            <h3 className="font-medium text-gray-900">Delivery Photo</h3>
            <span className="text-red-500">*</span>
          </div>
          
          {showCamera ? (
            <CameraCapture
              onCapture={handleCapture}
              onClose={() => setShowCamera(false)}
            />
          ) : photo ? (
            <div className="space-y-4">
              <div className="relative rounded-lg overflow-hidden">
                <img
                  src={photo}
                  alt="Delivery photo"
                  className="w-full h-64 object-cover"
                />
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={handleRetake}
                fullWidth
              >
                Retake Photo
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowCamera(true)}
              fullWidth
              className="h-32"
            >
              <div className="flex flex-col items-center gap-2">
                <Camera className="w-8 h-8" />
                <span>Take Photo</span>
              </div>
            </Button>
          )}
        </Card>
        
        {/* Note Section */}
        <Card>
          <div className="flex items-center gap-2 mb-4">
            <Package className="w-5 h-5 text-gray-600" />
            <h3 className="font-medium text-gray-900">Store Name</h3>
          </div>
          
          <Textarea
            placeholder="Enter the store name for this delivery..."
            rows={3}
            error={errors.note?.message}
            {...register('note')}
          />
        </Card>
        
        {/* Submit Button */}
        <Button
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          loading={isLoading}
          disabled={!photo || !location}
        >
          Submit Delivery
        </Button>
      </form>
    </div>
  )
}
